import { Component } from '@theme/component';

/**
 * @typedef {object} ProductTabsRefs
 * @property {HTMLButtonElement[]} [tabs] - Tab buttons, absent when the section has a single tab.
 * @property {HTMLElement[]} [panels] - Tab panels, in the same order as the tabs.
 */

/**
 * Switches between product carousels in the Tabbed products section.
 * Follows the WAI-ARIA tabs pattern with automatic activation.
 *
 * @extends {Component<ProductTabsRefs>}
 */
class ProductTabsComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    // Theme editor: show the panel of the block a merchant selects in the sidebar.
    this.addEventListener('shopify:block:select', this.#handleBlockSelect, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  select(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLButtonElement)) return;

    this.#activate(this.#tabs.indexOf(tab));
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const tabs = this.#tabs;
    const current = tabs.findIndex((tab) => tab === event.target);
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    const forward = isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRtl ? 'ArrowRight' : 'ArrowLeft';

    /** @type {Record<string, number>} */
    const targets = {
      [forward]: (current + 1) % tabs.length,
      [backward]: (current - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    };

    const next = targets[event.key];
    if (next === undefined) return;

    event.preventDefault();
    this.#activate(next, { focus: true });
  }

  /**
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const index = this.#panels.findIndex((panel) => panel === target || panel.contains(target));
    if (index !== -1) this.#activate(index);
  };

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #activate(index, { focus = false } = {}) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    if (index < 0 || index >= tabs.length) return;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = panels[i];
      if (panel) panel.hidden = !selected;
    }

    if (focus) tabs[index]?.focus();
  }

  get #tabs() {
    return this.refs.tabs ?? [];
  }

  get #panels() {
    return this.refs.panels ?? [];
  }
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
}
